import { useMe } from '@/lib/me';

/**
 * Retorna true si el usuario posee el permiso indicado.
 *
 * Los permisos son AUTORIDAD del backend: viven en la DB del QMS y se exponen por
 * `/me` (Auth0 = solo autenticación; el API tiene RBAC OFF y NO emite permisos en
 * el token). Por eso se leen de `useMe()`, no del access token (que viene vacío de
 * permisos). Fail-closed: false mientras `/me` no cargó o ante error.
 *
 * UX ONLY — el backend es el guardián real de cada operación (D4-008).
 */
export function usePermission(permission: string): boolean {
  const me = useMe();
  return me.data?.data.permissions.includes(permission) ?? false;
}

export function useAnyPermission(permissions: string[]): boolean {
  const me = useMe();
  const granted = me.data?.data.permissions ?? [];
  return permissions.some((p) => granted.includes(p));
}

/** Puede abrir documentos: por permiso de rol o por un acceso temporal de proveedor
 *  vigente (el backend limita qué documentos ve). Fail-closed mientras /me carga. */
export function useCanOpenDocuments(): boolean {
  const me = useMe();
  const data = me.data?.data;
  if (!data) {
    return false;
  }
  return (
    data.permissions.includes('quality:master-list:read') || data.document_access === 'granted'
  );
}

export type PermissionState = 'loading' | 'granted' | 'denied' | 'error';

/**
 * Estado del permiso para decidir QUÉ mostrar (no solo si mostrar): mientras `/me`
 * carga es `loading` (la pantalla muestra su esqueleto, nunca "sin acceso"); si `/me`
 * falla es `error` con reintento. Solo `granted` habilita las consultas.
 */
export function usePermissionState(permission: string): {
  state: PermissionState;
  retry: () => void;
  /** Reintentando tras un error (el panel muestra que está trabajando). */
  retrying: boolean;
  /** La falla de `/me` (null si no falló): el panel aconseja según la causa. */
  error: unknown;
} {
  const me = useMe();
  const retry = (): void => void me.refetch();
  const retrying = me.isFetching;
  const error = me.error;
  if (me.data) {
    return {
      state: me.data.data.permissions.includes(permission) ? 'granted' : 'denied',
      retry,
      retrying,
      error,
    };
  }
  if (me.isError) {
    return { state: 'error', retry, retrying, error };
  }
  return { state: 'loading', retry, retrying, error };
}
